import { authServiceApi } from "../../../../app/authServiceApi";
import { API_URL } from "../../../../constants/apiUrl";
import { CommonResponse, GetQueryArgsType } from "../../../../types";
import {
  AllKPIGroupParams,
  AllKPIGroupResponse,
  AllKPIGroupsByJobParams,
  AllKPIParams,
  AllKPIResponse,
  DownloadKpiExcelParams,
  SingleKPIGroup,
} from "./kpiSetup.type";

export const kpiSetupApi = authServiceApi.injectEndpoints({
  endpoints: (builder) => ({
    // ------ Mutations ------
    createKpiGroup: builder.mutation({
      query: (data) => ({
        url: API_URL.kpi,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetKPIGroup"];
        return [];
      },
    }),

    updateKPIGroup: builder.mutation({
      query: ({ data, id }) => ({
        url: `${API_URL.kpi}/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, _error, arg) => {
        if (result) {
          return ["GetKPIGroup", { type: "GetKPIGroup", id: arg.id }];
        }
        return [];
      },
    }),

    deleteKPIGroup: builder.mutation({
      query: ({ id }) => ({
        url: `${API_URL.kpi}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, _error) => {
        if (result) return ["GetKPIGroup"];
        return [];
      },
    }),

    createKPI: builder.mutation({
      query: ({ data, kpiGroupId }) => ({
        url: `${API_URL.kpi}/${kpiGroupId}/kpis`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result, _error, arg) => {
        if (result) return ["GetKPI", "GetKPIGroup"];
        return [];
      },
    }),

    updateKPI: builder.mutation({
      query: ({ data, kpiId, kpiGroupId }) => ({
        url: `${API_URL.kpi}/${kpiGroupId}/kpis/${kpiId}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, _error, arg) => {
        if (result) return ["GetKPI", "GetKPIGroup"];
        return [];
      },
    }),

    deleteKPI: builder.mutation({
      query: ({ kpiId, kpiGroupId }) => ({
        url: `${API_URL.kpi}/${kpiGroupId}/kpis/${kpiId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, _error, arg) => {
        if (result) return ["GetKPI", "GetKPIGroup"];
        return [];
      },
    }),

    giveKPIAchievement: builder.mutation({
      query: (data) => ({
        url: `${API_URL.giveKpiPerformance}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result, _error, arg) => {
        if (result) return ["GetKPI", "GetKPIGroup", "GetKPIAchievement"];
        return [];
      },
    }),

    // ------ Queries ------
    getAllKPIGroup: builder.query<
      CommonResponse<AllKPIGroupResponse>,
      GetQueryArgsType<AllKPIGroupParams>
    >({
      query: (queryArgs) => ({
        url: API_URL.kpi,
        ...queryArgs,
      }),
      providesTags: ["GetKPIGroup"],
    }),

    getAllKPIGroupByJob: builder.query<
      CommonResponse<AllKPIGroupResponse>,
      { args: GetQueryArgsType<AllKPIGroupsByJobParams>; id: number | string }
    >({
      query: ({ id, args: queryArgs }) => ({
        url: `${API_URL.getKpiGroupsByJob}/${id}/kpi-groups`,
        ...queryArgs,
      }),
      providesTags: ["GetKPIGroup"],
    }),

    getSingleKPIGroup: builder.query<CommonResponse<SingleKPIGroup>, number>({
      query: (id) => ({
        url: `${API_URL.kpi}/${id}`,
      }),
      providesTags: (_result, _error, id) => [{ type: "GetKPIGroup", id }],
    }),

    getAllKPI: builder.query<
      CommonResponse<AllKPIResponse>,
      { args: GetQueryArgsType<AllKPIParams>; kpiGroupId: string | number }
    >({
      query: ({ args, kpiGroupId }) => ({
        url: `${API_URL.kpi}/${kpiGroupId}/kpis`,
        ...args,
      }),
      providesTags: ["GetKPI"],
    }),

    // downloadKpiExcel: builder.query<
    //   Blob, // 👈 The endpoint returns a file, not JSON
    //   {
    //     args: GetQueryArgsType<DownloadKpiExcelParams>;
    //     kpiGroupId: string | number;
    //     jobId: string | number;
    //   }
    // >({
    //   query: ({ args, kpiGroupId, jobId }) => ({
    //     url: `/v1/kpi-setup/v1/job/${jobId}/kpi-groups/${kpiGroupId}/export`,
    //     ...args,
    //     responseHandler: async (response: any) => response?.blob(), // 👈 important line
    //   }),
    //   providesTags: ["GetKPI"],
    // }),
    downloadKpiExcel: builder.query<
      Blob,
      {
        args: GetQueryArgsType<DownloadKpiExcelParams>;
        kpiGroupId: string | number;
        jobId: string | number;
      }
    >({
      query: ({ args, kpiGroupId, jobId }) => ({
        url: `/v1/kpi-setup/v1/job/${jobId}/kpi-groups/${kpiGroupId}/export`,
        method: "GET",
        ...args,
        responseHandler: async (response: any) => {
          const contentType = response.headers.get("content-type");

          // If the server accidentally sends JSON (like error message), handle gracefully
          if (contentType?.includes("application/json")) {
            const json = await response.json();
            throw new Error(json?.message || "Failed to download Excel");
          }

          return response.blob(); // ✅ important
        },
      }),
    }),
  }),
});

export const {
  // ------ Mutation Hooks ------
  useCreateKpiGroupMutation,
  useUpdateKPIGroupMutation,
  useDeleteKPIGroupMutation,
  useCreateKPIMutation,
  useUpdateKPIMutation,
  useDeleteKPIMutation,
  useGiveKPIAchievementMutation,
  // ------ Query Hooks ------
  useGetAllKPIGroupQuery,
  useGetAllKPIGroupByJobQuery,
  useGetSingleKPIGroupQuery,
  useGetAllKPIQuery,
  useDownloadKpiExcelQuery,
  useLazyDownloadKpiExcelQuery,
} = kpiSetupApi;
